import React from "react";
import { SideMenu } from "@sumprio/ui";
import type { SideMenuItem } from "@sumprio/ui";
import "./Sidebar.scss";

export interface SidebarProps {
  menuItems: SideMenuItem[];
  open: boolean;
  activePathname: string;
  onNavigate: (_id: string) => void;
  onExpandChange: (_expanded: boolean) => void;
  onDrawerWidthChange: (_width: number) => void;
}

export default function Sidebar({
  menuItems,
  open,
  activePathname,
  onNavigate,
  onExpandChange,
  onDrawerWidthChange,
}: SidebarProps) {
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width;
      if (typeof width === "number") onDrawerWidthChange(width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [onDrawerWidthChange]);

  const activeId = (() => {
    if (activePathname === "/search-result" || activePathname.startsWith("/search-result")) {
      return "/search";
    }
    if (activePathname === "/feedback-detail" || activePathname.startsWith("/feedback-detail/")) {
      return "/feedback";
    }
    if (
      activePathname.startsWith("/groups/case-group/") ||
      activePathname.startsWith("/groups/client-group/")
    ) {
      return "/groups";
    }
    if (activePathname.startsWith("/clients/") || activePathname === "/clients") {
      return "/clients";
    }
    if (activePathname === "/case-details" || activePathname.startsWith("/case-details/")) {
      return "/cases";
    }
    if (activePathname.startsWith("/categories/") || activePathname === "/categories") {
      return "/categories";
    }
    if (activePathname.startsWith("/business/")) {
      return "/businesses";
    }
    if (
      activePathname === "/user" ||
      activePathname.startsWith("/user/") ||
      activePathname.startsWith("/users/")
    ) {
      return "/users";
    }
    return activePathname;
  })();

  return (
    <div className="sidebar" ref={ref}>
      <SideMenu
        items={menuItems}
        expanded={open}
        activeId={activeId}
        onChange={(id) => {
          if (!id.startsWith("_div")) onNavigate(id);
        }}
        onExpandChange={(expanded) => {
          if (expanded !== open) onExpandChange(expanded);
        }}
      />
    </div>
  );
}
